import { z } from 'zod';

import { languageCodeSchema } from '../internationalization';
import type { CustomFieldPrefix, Mutation } from './common';
import type { CustomAttributes } from './custom-attribute';
import { customAttributeOptionsSchema, customAttributesSchema } from './custom-attribute';
import type { CustomFilterCriteria, FilterCriteria, FilterCriteriaDto } from './filter';
import { paginationSchema } from './pagination';
import { seniorityLevelSchema } from './seniority-level';
import type { SkillMutation } from './skill';
import { skillMutationSchema, skillSchema, skillShortSchema } from './skill';
import { baseUserSchema, contactUserSchema, userSchema } from './user';

export const jobStateSchema = z.union([
    z.literal('draft'),
    z.literal('published'),
    z.literal('hired'),
    z.literal('archived'),
]);

export type JobState = z.infer<typeof jobStateSchema>;

export const jobWorkPlaceSchema = z.union([z.literal('remote'), z.literal('on-site')]);

export type JobWorkPlace = z.infer<typeof jobWorkPlaceSchema>;

export const jobDurationSchema = z.union([z.literal('permanent'), z.literal('fixed-term')]);

export type JobDuration = z.infer<typeof jobDurationSchema>;

export const jobTranslationsSchema = z.object({
    description: z.string().optional(),
    language: languageCodeSchema,
    title: z.string(),
});

export const baseJobSchema = z.object({
    id: z.string(),
    title: z.string(),
});

export type BaseJob = z.infer<typeof baseJobSchema>;

const jobContactSchema = z.object({
    emailAddress: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
});

export type JobContact = z.infer<typeof jobContactSchema>;

export const jobSchema = baseJobSchema.extend({
    applicationUrl: z.string().optional(),
    contact: jobContactSchema.optional(),
    creator: contactUserSchema.optional(),
    custom: customAttributesSchema.optional(),
    description: z.string().optional(),
    duration: jobDurationSchema.optional(),
    hiringManager: baseUserSchema.optional().nullable(),
    isNew: z.boolean(),
    language: languageCodeSchema.optional(),
    owner: contactUserSchema.optional().nullable(),
    publishedAt: z.string().optional(),
    seniorityLevel: seniorityLevelSchema,
    shortListSize: z.number().optional(),
    skills: skillSchema.array().optional().default([]),
    startDate: z.string().optional(),
    state: jobStateSchema,
    translations: jobTranslationsSchema.array(),
    updatedAt: z.string(),
    workPlace: jobWorkPlaceSchema,
});

export type Job = z.infer<typeof jobSchema>;

export const jobPaginatedSchema = z.object({
    data: jobSchema.array(),
    pagination: paginationSchema,
});

export const jobUpdateRequestSchema = z.object({
    applicationUrl: z.string().optional(),
    custom: customAttributesSchema.optional(),
    duration: jobDurationSchema.optional(),
    hiringManager: z.uuid().optional(),
    owner: z.uuid().optional(),
    seniorityLevel: z.number(),
    skills: skillMutationSchema.array().optional(),
    startDate: z.string().optional(),
    state: jobStateSchema,
    translations: jobTranslationsSchema.array(),
    workPlace: jobWorkPlaceSchema,
});

export type JobPaginated = z.infer<typeof jobPaginatedSchema>;

export const jobCreateRequestSchema = jobUpdateRequestSchema.extend({
    externalId: z.string().max(128).optional(),
    publishedAt: z.string().datetime({ offset: true }).optional(),
});

export const jobGenerateDescriptionRequestSchema = jobUpdateRequestSchema.pick({
    custom: true,
    skills: true,
    translations: true,
    workPlace: true,
});

export type JobCreateRequest = z.infer<typeof jobCreateRequestSchema>;

export type JobUpdateRequest = z.infer<typeof jobUpdateRequestSchema>;

export type JobGenerateDescriptionRequest = z.infer<typeof jobGenerateDescriptionRequestSchema>;

export type JobTranslation = z.infer<typeof jobTranslationsSchema>;

export type JobMutation = Mutation<
    Omit<
        Job,
        'custom' | 'description' | 'hiringManager' | 'isNew' | 'language' | 'owner' | 'skills' | 'title' | 'updatedAt'
    >
> & {
    readonly custom?: Mutation<CustomAttributes> | undefined;
    readonly hiringManager: string | null;
    readonly owner: string | null;
    readonly skills: readonly SkillMutation[];
    readonly translations: readonly JobTranslation[];
};

export type JobPatchMutation = Partial<JobMutation> & Pick<Job, 'id'>;

export const jobCandidateSchema = userSchema.extend({
    isOnShortList: z.boolean().optional(),
    matchingScore: z.number().optional(),
    matchingSkills: skillShortSchema.array().optional(),
    skills: skillShortSchema.array().optional(),
});

export type JobCandidate = z.infer<typeof jobCandidateSchema>;

export const jobCandidatePaginatedSchema = z.object({
    data: jobCandidateSchema.array(),
    pagination: paginationSchema,
});

export const jobShortListCandidateSchema = jobCandidateSchema.extend({
    notes: z.string().optional(),
    recruiter: userSchema.optional(),
});
export const jobShortListCandidateArraySchema = jobShortListCandidateSchema.array();

export type JobShortListCandidate = z.infer<typeof jobShortListCandidateSchema>;

type JobFilterOptionsCustomFields = Record<`${typeof CustomFieldPrefix}${string}`, CustomFilterCriteria['type']>;

type JobFilterTypes = Job & JobFilterOptionsCustomFields;

export type JobFilterCriteria<Key extends keyof JobFilterTypes = keyof JobFilterTypes> =
    | CustomFilterCriteria
    | FilterCriteria<JobFilterTypes, Key>;

export type JobFilterCriteriaDto = FilterCriteriaDto<JobFilterCriteria>;

export const jobGenerationResponseSchema = z.object({
    jobDescription: z.string().optional(),
});

export type JobGenerationResponse = z.infer<typeof jobGenerationResponseSchema>;

export const singleJobOptionsSchema = z.object({
    canDeleteJob: z.boolean(),
    canEditJob: z.boolean(),
    canEditShortlist: z.boolean(),
    canExportShortlist: z.boolean(),
    canReadCandidates: z.boolean(),
    canReadJob: z.boolean(),
    canReadShortlist: z.boolean(),
});

export type SingleJobOptions = z.infer<typeof singleJobOptionsSchema>;

export const jobsOptionsSchema = z.object({
    canCreateJobs: z.boolean(),
    canManageJobs: z.boolean(),
    customAttributes: customAttributeOptionsSchema.array(),
});
export type JobsOptions = z.infer<typeof jobsOptionsSchema>;
